export interface SectionFeedback {
  sectionName: string;
  rating: number; // 1-5
  isUseful: boolean;
  accuracyRating: number; // 1-5
  comment: string;
}

export interface CreateFeedbackRequest {
  assessmentId: string;
  assessmentType?: string;
  // User context (optional as backend can get from auth)
  userContext?: {
    id: string;
    name: string;
    email: string;
    profileImage?: string;
    subscriptionTier: string;
  };
  // Feedback data
  overallRating: number; // 1-5 stars
  accuracyRating: number; // 1-5
  satisfactionRating: number; // 1-5
  generalComment: string;
  sectionFeedbacks: Record<string, SectionFeedback>;
  usefulnessFlags: Record<string, boolean>;
  improvementAreas: string[];
  wouldRecommend: boolean;
  // Metadata
  submittedAt?: string;
  userAgent?: string;
}

export interface AssessmentFeedback extends CreateFeedbackRequest {
  id: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackSummary {
  assessmentType: string;
  totalFeedbacks: number;
  averageOverallRating: number;
  averageAccuracyRating: number;
  averageSatisfaction: number;
  recommendationRate: number;
  sectionRatings: Record<string, number>;
  commonImprovementAreas: Array<{
    area: string;
    count: number;
    frequency: number;
  }>;
  lastUpdated: string;
}

class FeedbackService {
  private baseUrl = '/api/v2';

  async createFeedback(feedbackData: CreateFeedbackRequest): Promise<{ message: string; feedbackId: string }> {
    const response = await fetch(`${this.baseUrl}/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify({
        assessment_id: feedbackData.assessmentId,
        overall_rating: feedbackData.overallRating,
        accuracy_rating: feedbackData.accuracyRating,
        satisfaction_rating: feedbackData.satisfactionRating,
        general_comment: feedbackData.generalComment,
        section_feedbacks: this.convertSectionFeedbacks(feedbackData.sectionFeedbacks),
        usefulness_flags: feedbackData.usefulnessFlags,
        improvement_areas: feedbackData.improvementAreas,
        would_recommend: feedbackData.wouldRecommend,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create feedback');
    }

    return response.json();
  }

  async getFeedbackByAssessment(assessmentId: string): Promise<AssessmentFeedback | null> {
    const response = await fetch(`${this.baseUrl}/assessments/${assessmentId}/feedback`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get feedback');
    }

    return response.json();
  }

  async getUserFeedbacks(): Promise<{ feedbacks: AssessmentFeedback[]; total: number }> {
    const response = await fetch(`${this.baseUrl}/user/feedbacks`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get user feedbacks');
    }

    return response.json();
  }

  async updateFeedback(feedbackId: string, feedbackData: CreateFeedbackRequest): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/feedback/${feedbackId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify({
        assessment_id: feedbackData.assessmentId,
        overall_rating: feedbackData.overallRating,
        accuracy_rating: feedbackData.accuracyRating,
        satisfaction_rating: feedbackData.satisfactionRating,
        general_comment: feedbackData.generalComment,
        section_feedbacks: this.convertSectionFeedbacks(feedbackData.sectionFeedbacks),
        usefulness_flags: feedbackData.usefulnessFlags,
        improvement_areas: feedbackData.improvementAreas,
        would_recommend: feedbackData.wouldRecommend,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update feedback');
    }

    return response.json();
  }

  async getFeedbackSummary(assessmentType: string = 'communication'): Promise<FeedbackSummary> {
    const response = await fetch(`${this.baseUrl}/admin/feedback/summary?type=${assessmentType}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get feedback summary');
    }

    return response.json();
  }

  async createQuickFeedback(feedbackData: QuickFeedbackRequest): Promise<{ message: string; feedbackId: string }> {
    const response = await fetch(`${this.baseUrl}/feedback/quick`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify({
        assessment_id: feedbackData.context.assessmentId,
        emoji_score: feedbackData.rating,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create quick feedback');
    }

    return response.json();
  }

  private convertSectionFeedbacks(sectionFeedbacks: Record<string, SectionFeedback>) {
    const converted: Record<string, any> = {};
    for (const [key, feedback] of Object.entries(sectionFeedbacks)) {
      converted[key] = {
        section_name: feedback.sectionName,
        rating: feedback.rating,
        is_useful: feedback.isUseful,
        accuracy_rating: feedback.accuracyRating,
        comment: feedback.comment,
      };
    }
    return converted;
  }
}

export const feedbackService = new FeedbackService();

// Quick feedback interface for EmojiRating component
export interface QuickFeedbackRequest {
  rating: number;
  comment?: string;
  context: {
    component: string;
    feature: string;
    assessmentId?: string;
    assessmentType?: string;
  };
  userContext?: {
    id: string;
    name: string;
    email: string;
    profileImage?: string;
    subscriptionTier: string;
  };
  submittedAt?: string;
  userAgent?: string;
}

